/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ProgressBar — Reusable progress bar component for the CoreX design system.
 *
 * Usage:
 *   <ProgressBar value={75} color="var(--color-success)" />
 *   <ProgressBar value={50} color="#F59E0B" height={6} label="50%" />
 *   <ProgressBar value={25} /> // defaults to brand-primary
 */

interface ProgressBarProps {
  /** Progress value (0–100) */
  value: number;
  /** Bar fill color — CSS color string. Defaults to var(--color-brand-primary) */
  color?: string;
  /** Track height in px. Default: 4 */
  height?: number;
  /** Optional label displayed to the right of the bar */
  label?: string;
  /** Whether to show the percentage text inside/beside the bar */
  showPercent?: boolean;
  /** Animate the fill on mount. Default: true */
  animated?: boolean;
  /** Additional className for the wrapper */
  className?: string;
}

export default function ProgressBar({
  value,
  color,
  height = 4,
  label,
  showPercent = false,
  animated = true,
  className = '',
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, value));

  return (
    <div className={`corex-progress ${className}`}>
      <div className="corex-progress-track" style={{ height }}>
        <div
          className={`corex-progress-fill ${animated ? 'corex-progress-fill--animated' : ''}`}
          style={{
            width: `${clamped}%`,
            backgroundColor: color || 'var(--color-brand-primary)',
            height,
          }}
        />
      </div>
      {(label || showPercent) && (
        <span className="corex-progress-label">
          {label || `${Math.round(clamped)}%`}
        </span>
      )}
      <style>{`
        .corex-progress {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
        }
        .corex-progress-track {
          flex: 1;
          background: var(--color-bg-primary);
          border-radius: var(--radius-full);
          overflow: hidden;
        }
        .corex-progress-fill {
          border-radius: var(--radius-full);
          transition: none;
        }
        .corex-progress-fill--animated {
          transition: width 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .corex-progress-label {
          font-size: 0.6875rem;
          font-weight: 500;
          color: var(--color-text-muted);
          font-family: var(--font-mono);
          white-space: nowrap;
          min-width: 28px;
          text-align: right;
        }
        @media (prefers-reduced-motion: reduce) {
          .corex-progress-fill--animated {
            transition: none;
          }
        }
      `}</style>
    </div>
  );
}

export { type ProgressBarProps };
